<template>
  <div class="withdraw-audit-page">
    <header class="page-hero">
      <div>
        <span class="eyebrow">Withdraw Audit</span>
        <h2>提现财务审计</h2>
        <p>审核用户提现申请，核对收款信息并记录审批结果。</p>
      </div>
      <el-segmented v-model="queryParams.status" :options="statusOptions" class="status-segment"
        @change="handleFilterChange" />
    </header>

    <section class="stats-grid">
      <article class="stat-card primary">
        <span>{{ currentStatusLabel }}申请</span>
        <strong>{{ total }}</strong>
      </article>
      <article class="stat-card">
        <span>本页提现金额</span>
        <strong>￥{{ pageAmountTotal.toFixed(2) }}</strong>
      </article>
      <article class="stat-card">
        <span>本页申请人数</span>
        <strong>{{ pageUserCount }}</strong>
      </article>
      <article class="stat-card action-card">
        <span>列表操作</span>
        <el-button class="ghost-btn" :loading="loading" @click="getList">刷新列表</el-button>
      </article>
    </section>

    <section class="content-panel">
      <div class="table-title">
        <div>
          <h3>{{ currentStatusLabel }}提现列表</h3>
          <p>请核对手机号、真实姓名、银行账号与提现金额后再审批。</p>
        </div>
        <span>共 {{ total }} 条记录</span>
      </div>

      <el-table :data="tableData" v-loading="loading" class="audit-table" height="calc(100vh - 365px)">
        <el-table-column prop="id" label="单号" width="90" align="center" />

        <el-table-column label="申请用户" min-width="180">
          <template #default="{ row }">
            <div class="user-cell">
              <strong>{{ row.name || '-' }}</strong>
              <span>{{ row.user_phone || '-' }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="收款信息" min-width="260">
          <template #default="{ row }">
            <div class="account-cell">
              <div><span>银行</span><strong>{{ row.bankname || '-' }}</strong></div>
              <div><span>账号</span><strong>{{ row.account || '-' }}</strong></div>
              <div v-if="row.wechat"><span>微信</span><strong>{{ row.wechat }}</strong></div>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="amount" label="提现金额" width="140" align="center">
          <template #default="{ row }">
            <span class="highlight-amount">￥{{ Number(row.amount || 0).toFixed(2) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="110" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.status === 0" type="warning" effect="dark">待审核</el-tag>
            <el-tag v-else-if="row.status === 1" type="success" effect="dark">已通过</el-tag>
            <el-tag v-else type="danger" effect="dark">已驳回</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="create_time" label="申请时间" width="180" align="center">
          <template #default="{ row }">
            {{ formatTime(row.create_time) }}
          </template>
        </el-table-column>

        <el-table-column v-if="queryParams.status === 2" prop="remark" label="驳回原因" min-width="220"
          show-overflow-tooltip />

        <el-table-column v-if="queryParams.status === 0" label="操作" width="180" align="center" fixed="right">
          <template #default="{ row }">
            <div class="row-actions">
              <el-button class="pass-btn" size="small" @click="handlePass(row)">通过</el-button>
              <el-button class="reject-btn" size="small" @click="openRejectDialog(row)">驳回</el-button>
            </div>
          </template>
        </el-table-column>

        <template #empty>
          <el-empty description="暂无提现申请" />
        </template>
      </el-table>

      <div class="pagination-box">
        <el-pagination v-model:current-page="queryParams.page" v-model:page-size="queryParams.pageSize"
          :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper" :total="total"
          @size-change="handlePageSizeChange" @current-change="getList" />
      </div>
    </section>

    <el-dialog v-model="rejectDialog.visible" title="驳回提现申请" width="520px" class="withdraw-dialog">
      <div class="reject-info">
        <span>用户手机号</span>
        <strong>{{ rejectDialog.phone || '-' }}</strong>
      </div>
      <el-input v-model="rejectDialog.reason" type="textarea" :rows="5" maxlength="300" show-word-limit resize="none"
        placeholder="请输入驳回原因，用户可据此修改后重新提交" />
      <template #footer>
        <el-button class="ghost-btn" @click="rejectDialog.visible = false">取消</el-button>
        <el-button type="danger" :loading="rejectDialog.loading" @click="submitReject">确认驳回</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

const loading = ref(false)
const tableData = ref([])
const total = ref(0)

const statusOptions = [
  { label: '待审核', value: 0 },
  { label: '已通过', value: 1 },
  { label: '已驳回', value: 2 }
]

const queryParams = reactive({
  page: 1,
  pageSize: 10,
  status: 0
})

const rejectDialog = reactive({
  visible: false,
  phone: '',
  reason: '',
  loading: false
})

const currentStatusLabel = computed(() => statusOptions.find(item => item.value === queryParams.status)?.label || '提现')
const pageAmountTotal = computed(() => tableData.value.reduce((sum, item) => sum + Number(item.amount || 0), 0))
const pageUserCount = computed(() => new Set(tableData.value.map(item => item.user_phone).filter(Boolean)).size)

const getList = async () => {
  loading.value = true
  try {
    const res = await axios.post('/admin/getWithdrawList', {
      page: queryParams.page,
      pageSize: queryParams.pageSize,
      status: queryParams.status
    })

    if (res.data.msg === 'success') {
      tableData.value = res.data.data || []
      total.value = Number(res.data.total || 0)
    } else {
      ElMessage.error(res.data.message || '获取列表失败')
    }
  } catch (error) {
    console.error(error)
    ElMessage.error('网络请求失败')
  } finally {
    loading.value = false
  }
}

const handleFilterChange = () => {
  queryParams.page = 1
  getList()
}

const handlePageSizeChange = () => {
  queryParams.page = 1
  getList()
}

const handlePass = (row) => {
  ElMessageBox.confirm(`确定通过 ${row.name || row.user_phone} 的 ￥${Number(row.amount || 0).toFixed(2)} 提现申请吗？`, '财务审批', {
    confirmButtonText: '确认通过',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      const res = await axios.post('/admin/backWithdraw', {
        phone: row.user_phone,
        action: 'pass'
      })

      if (res.data.msg === 'success') {
        ElMessage.success('审批通过，已计入用户已提现金额')
        getList()
      } else {
        ElMessage.error(res.data.message || '操作失败')
      }
    } catch (error) {
      ElMessage.error('接口请求报错')
    }
  }).catch(() => { })
}

const openRejectDialog = (row) => {
  rejectDialog.phone = row.user_phone
  rejectDialog.reason = ''
  rejectDialog.visible = true
}

const submitReject = async () => {
  if (!rejectDialog.reason.trim()) {
    ElMessage.warning('请输入驳回原因')
    return
  }

  rejectDialog.loading = true
  try {
    const res = await axios.post('/admin/backWithdraw', {
      phone: rejectDialog.phone,
      action: 'reject',
      reason: rejectDialog.reason.trim()
    })

    if (res.data.msg === 'success') {
      ElMessage.success('驳回成功')
      rejectDialog.visible = false
      getList()
    } else {
      ElMessage.error(res.data.message || '驳回失败')
    }
  } catch (error) {
    ElMessage.error('接口请求报错')
  } finally {
    rejectDialog.loading = false
  }
}

const formatTime = (time) => {
  if (!time) return '-'
  return String(time).replace('T', ' ').split('.')[0]
}

onMounted(() => {
  getList()
})
</script>

<style scoped>
.withdraw-audit-page {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  padding: 24px;
  background: #070b12;
  color: #e8edf7;
  display: flex;
  flex-direction: column;
}

:global(.el-main:has(.withdraw-audit-page)) {
  height: calc(100vh - 60px);
  padding: 0;
  overflow: hidden;
  background: #070b12;
}

.page-hero {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-end;
  padding: 24px;
  border: 1px solid #202938;
  border-radius: 10px;
  background: linear-gradient(135deg, #111827 0%, #0b111d 58%, #151018 100%);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.28);
  margin-bottom: 18px;
}

.page-hero h2 {
  margin: 6px 0 8px;
  color: #ffffff;
  font-size: 28px;
  line-height: 1.15;
}

.page-hero p {
  margin: 0;
  color: #8f9bad;
}

.eyebrow {
  color: #ff9b4a;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.status-segment {
  min-width: 330px;
  --el-segmented-bg-color: #151d2b;
  --el-segmented-item-selected-bg-color: rgba(255, 116, 23, 0.18);
  --el-segmented-item-selected-color: #ff9b4a;
  --el-segmented-item-hover-bg-color: rgba(255, 255, 255, 0.05);
}

.stats-grid {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.stat-card {
  min-height: 96px;
  padding: 18px;
  box-sizing: border-box;
  border: 1px solid #202938;
  border-radius: 10px;
  background: #0d131f;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.22);
}

.stat-card.primary {
  border-color: rgba(255, 116, 23, 0.35);
  background: linear-gradient(135deg, rgba(255, 116, 23, 0.22), #101827 65%);
}

.stat-card span {
  display: block;
  color: #8f9bad;
  font-size: 13px;
  margin-bottom: 10px;
}

.stat-card strong {
  color: #ffffff;
  font-size: 26px;
  line-height: 1.2;
}

.action-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.content-panel {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #202938;
  border-radius: 10px;
  background: #0d131f;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.24);
  padding: 18px;
}

.table-title {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-end;
  padding-bottom: 16px;
}

.table-title h3 {
  margin: 0 0 5px;
  color: #f5f7fb;
  font-size: 18px;
}

.table-title p,
.table-title span {
  margin: 0;
  color: #8f9bad;
  font-size: 13px;
}

.audit-table {
  flex: 1;
  min-height: 260px;
  border-radius: 8px;
  overflow: hidden;
}

.user-cell,
.account-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-cell strong {
  color: #f5f7fb;
}

.user-cell span {
  color: #8f9bad;
  font-size: 12px;
}

.account-cell div {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 8px;
  line-height: 1.5;
}

.account-cell span {
  color: #7f8a9c;
}

.account-cell strong {
  color: #d8dfeb;
  font-weight: 500;
  word-break: break-all;
}

.highlight-amount {
  color: #fbbf24;
  font-weight: 700;
  font-size: 15px;
}

.row-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.pass-btn,
.reject-btn,
.ghost-btn {
  border-radius: 6px;
}

.pass-btn {
  border-color: rgba(34, 197, 94, 0.42);
  background: rgba(22, 101, 52, 0.22);
  color: #86efac;
}

.pass-btn:hover {
  border-color: #22c55e;
  background: rgba(22, 163, 74, 0.36);
  color: #ffffff;
}

.reject-btn {
  border-color: rgba(248, 113, 113, 0.38);
  background: rgba(127, 29, 29, 0.18);
  color: #fca5a5;
}

.reject-btn:hover {
  border-color: #ef4444;
  background: rgba(185, 28, 28, 0.42);
  color: #ffffff;
}

.ghost-btn {
  border-color: #314059;
  background: #121a28;
  color: #d6deea;
}

.ghost-btn:hover {
  border-color: #ff7417;
  color: #ff9b4a;
  background: #161f2f;
}

.pagination-box {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
}

.reject-info {
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr);
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #263142;
  border-radius: 8px;
  background: #0d131f;
  margin-bottom: 14px;
}

.reject-info span {
  color: #8f9bad;
}

.reject-info strong {
  color: #f5f7fb;
}

:deep(.el-table),
:deep(.el-table tr),
:deep(.el-table th.el-table__cell),
:deep(.el-table td.el-table__cell) {
  background-color: #101827;
  color: #e5eaf3;
  border-bottom-color: #263142;
}

:deep(.el-table th.el-table__cell) {
  background-color: #121a28;
  color: #9fb0ca;
  font-weight: 700;
}

:deep(.el-table tbody tr:hover > td.el-table__cell) {
  background-color: #172236 !important;
}

:deep(.el-table::before),
:deep(.el-table__inner-wrapper::before) {
  background-color: #263142;
}

:deep(.el-textarea__inner) {
  background: #121a28;
  border: 1px solid #2b374b;
  box-shadow: none;
  color: #e8edf7;
}

:deep(.el-textarea__inner:focus),
:deep(.el-textarea__inner:hover) {
  border-color: #ff7417;
}

:deep(.el-input__count),
:deep(.el-input__count-inner),
:deep(.el-textarea .el-input__count),
:deep(.el-textarea .el-input__count-inner) {
  background: transparent !important;
  color: #667286;
}

:deep(.el-pagination) {
  --el-pagination-bg-color: #121a28;
  --el-pagination-button-disabled-bg-color: #0d131f;
  --el-pagination-hover-color: #ff9b4a;
  --el-pagination-text-color: #cbd5e1;
  --el-pagination-button-color: #cbd5e1;
}

:deep(.el-empty__description p) {
  color: #8f9bad;
}

@media (max-width: 1200px) {
  .page-hero {
    align-items: stretch;
    flex-direction: column;
  }

  .status-segment {
    min-width: 0;
    width: 100%;
  }

  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
